import type React from "react";
import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, type PieSectorDataItem } from "recharts";
import { Card, CardLabel } from "./Card";

export interface DonutDatum {
  name: string;
  value: number;
  color: string;
}

interface DonutWithLegendProps {
  title: string;
  data: DonutDatum[];
  centerValue: string | number;
  centerLabel: string;
  delay: number;
  accentCenter?: string;
}

const RADIAN = Math.PI / 180;
// Rough size of the rendered tooltip box -- recharts doesn't expose the real
// measured size as a prop, so this is used to center the box on the outward
// anchor point regardless of which direction it points.
const TOOLTIP_WIDTH_ESTIMATE = 90;
const TOOLTIP_HEIGHT_ESTIMATE = 48;

// The center label is a plain CSS-positioned overlay, invisible to recharts,
// so its default tooltip (anchored near the cursor, inside the ring) can end
// up sitting right on top of it. Instead we track the hovered slice's
// midAngle and force the tooltip out past outerRadius in that same
// direction, then center the box on that outward point -- rather than
// treating it as the box's corner -- so it clears the center label evenly no
// matter which way the slice points.
interface HoveredSlice {
  cx: number;
  cy: number;
  outerRadius: number;
  midAngle: number | undefined;
}

function outwardTooltipPosition(slice: HoveredSlice | null): { x: number; y: number } | undefined {
  if (slice?.midAngle === undefined) return undefined;
  const angle = -slice.midAngle * RADIAN;
  const margin = 16;
  const anchorX = slice.cx + (slice.outerRadius + margin) * Math.cos(angle);
  const anchorY = slice.cy + (slice.outerRadius + margin) * Math.sin(angle);
  return {
    x: anchorX - TOOLTIP_WIDTH_ESTIMATE / 2,
    y: anchorY - TOOLTIP_HEIGHT_ESTIMATE / 2,
  };
}

export function DonutWithLegend({
  title,
  data,
  centerValue,
  centerLabel,
  delay,
  accentCenter,
}: DonutWithLegendProps): React.JSX.Element {
  const [hoveredSlice, setHoveredSlice] = useState<HoveredSlice | null>(null);
  const tooltipPosition = outwardTooltipPosition(hoveredSlice);

  return (
    <Card delay={delay}>
      <CardLabel>{title}</CardLabel>
      <div className="flex items-center gap-6">
        <div className="relative" style={{ width: 200, height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={65}
                outerRadius={95}
                paddingAngle={3}
                stroke="none"
                animationBegin={delay}
                animationDuration={900}
                onMouseEnter={(entry: PieSectorDataItem) => {
                  setHoveredSlice({
                    cx: entry.cx,
                    cy: entry.cy,
                    outerRadius: entry.outerRadius,
                    midAngle: entry.midAngle,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredSlice(null);
                }}
              >
                {data.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                {...(tooltipPosition ? { position: tooltipPosition } : {})}
                contentStyle={{
                  fontFamily: "Inter",
                  fontSize: 12,
                  borderRadius: 8,
                  background: "#1f1f1f",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span
              className="font-head font-extrabold text-4xl leading-none"
              style={{ color: accentCenter ?? "#FFFFFF" }}
            >
              {centerValue}
            </span>
            <span className="text-white/40 text-[10px] font-body uppercase tracking-wider mt-1">
              {centerLabel}
            </span>
          </div>
        </div>
        <div className="flex-1 space-y-2.5">
          {data.map((d) => (
            <div key={d.name} className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
              <span className="text-white/70 text-sm font-body flex-1">{d.name}</span>
              <span className="text-white font-head font-semibold text-sm">{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
